import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import SearchSelect from '../components/ui/SearchSelect';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { Plus, ShoppingBag, Trash2, Eye, X, FileText, Calendar, Edit, DollarSign, Download, AlertTriangle, Building2, Bot, Award, Package, TrendingUp, TrendingDown, Send, Loader2, ChevronDown, ChevronUp, Truck, RefreshCw, CheckCircle } from 'lucide-react';

// Simple Modal Component
const Modal = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-background rounded-lg shadow-xl w-full ${wide ? 'max-w-3xl' : 'max-w-md'} mx-4 p-6 z-10 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-chivo font-extrabold">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Purchases = () => {
  const { api } = useAuth();
  const { isMatriz, branchName, currentBranch } = useBranch();
  const [purchases, setPurchases] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState(false);
  
  // New Purchase Dialog
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    supplier: '',
    invoice_number: '',
    payment_method: 'efectivo',
    payment_source: 'caja_principal',
    notes: '',
    items: [],
    fecha: new Date().toISOString().slice(0, 10) // Custom date field
  });
  
  // View Purchase Dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingPurchase, setViewingPurchase] = useState(null);

  // Edit Purchase Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [editForm, setEditForm] = useState({
    supplier: '',
    invoice_number: '',
    payment_method: '',
    notes: '',
    fecha: '',
    items: []
  });

  // Caja Chica balance
  const [cajaChicaBalance, setCajaChicaBalance] = useState(0);

  // Date filter state
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });

  // Tab state for Purchases vs Agent
  const [activeTab, setActiveTab] = useState('compras');
  
  // Purchase Agent state
  const [agentDashboard, setAgentDashboard] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null); // 'proveedores', 'gastado', 'urgentes'
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [completedPurchases, setCompletedPurchases] = useState({}); // Track completed day+supplier combos
  const chatEndRef = useRef(null);

  // Cost Agent state
  const [costDashboard, setCostDashboard] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costChatMessages, setCostChatMessages] = useState([]);
  const [costChatInput, setCostChatInput] = useState('');
  const [costChatLoading, setCostChatLoading] = useState(false);
  const [showAllSupplies, setShowAllSupplies] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null); // For product detail modal
  const costChatEndRef = useRef(null);

  // Generate shopping list for a specific day and supplier
  const generateShoppingList = (dayData, supplierName, supplierItems) => {
    const stockNeeds = agentDashboard?.stock_needs || [];
    let listContent = `LISTA DE COMPRAS - ${dayData.day_name.toUpperCase()}\n`;
    listContent += `Proveedor: ${supplierName}\n`;
    listContent += `Fecha generada: ${new Date().toLocaleDateString('es-MX')}\n`;
    listContent += `${'='.repeat(40)}\n\n`;
    
    supplierItems.forEach(itemName => {
      const stockData = stockNeeds.find(n => n.name === itemName);
      const itemData = agentDashboard?.best_buy_days?.[itemName];
      if (stockData || itemData) {
        const qty = stockData?.suggested_qty || '-';
        const unit = stockData?.unit || '';
        const price = itemData?.best_supplier_price || itemData?.avg_price || '-';
        listContent += `☐ ${itemName}\n`;
        listContent += `   Cantidad: ${qty} ${unit}\n`;
        listContent += `   Precio ref: $${price}\n\n`;
      }
    });
    
    listContent += `${'='.repeat(40)}\n`;
    listContent += `Total estimado: $${dayData.total_gastado?.toLocaleString() || 'N/A'}\n`;
    
    // Create and download text file
    const blob = new Blob([listContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lista_compras_${dayData.day_name}_${supplierName.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Lista generada para ${supplierName}`);
  };

  // Mark a day+supplier purchase as completed
  const markPurchaseCompleted = (dayName, supplierName) => {
    const key = `${dayName}_${supplierName}`;
    setCompletedPurchases(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    toast.success(completedPurchases[`${dayName}_${supplierName}`] 
      ? `Desmarcado: ${supplierName}` 
      : `Completado: ${supplierName} (${dayName})`);
  };

  useEffect(() => {
    fetchData();
  }, [currentBranch]);

  useEffect(() => {
    if (activeTab === 'agente' && !agentDashboard) {
      fetchAgentDashboard();
    }
    if (activeTab === 'costos' && !costDashboard) {
      fetchCostDashboard();
    }
  }, [activeTab]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    costChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [costChatMessages]);

  const fetchAgentDashboard = async () => {
    setAgentLoading(true);
    try {
      const res = await api.get('/purchase-agent/dashboard');
      setAgentDashboard(res.data);
    } catch (error) {
      toast.error('Error al cargar agente');
    } finally {
      setAgentLoading(false);
    }
  };

  const fetchCostDashboard = async () => {
    setCostLoading(true);
    try {
      const res = await api.get('/cost-agent/dashboard');
      setCostDashboard(res.data);
    } catch (error) {
      toast.error('Error al cargar agente de costos');
    } finally {
      setCostLoading(false);
    }
  };

  const sendAgentMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);
    try {
      const res = await api.post('/purchase-agent/chat', { message: userMsg });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      toast.error('Error en el chat');
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Error. Intenta de nuevo.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const sendCostMessage = async () => {
    if (!costChatInput.trim() || costChatLoading) return;
    const userMsg = costChatInput.trim();
    setCostChatInput('');
    setCostChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setCostChatLoading(true);
    try {
      const res = await api.post('/cost-agent/chat', { message: userMsg });
      setCostChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      toast.error('Error en el chat');
      setCostChatMessages(prev => [...prev, { role: 'assistant', content: 'Error. Intenta de nuevo.' }]);
    } finally {
      setCostChatLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case 'urgent': return <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">URGENTE</span>;
      case 'soon': return <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">PRONTO</span>;
      default: return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">NORMAL</span>;
    }
  };

  const getAlertColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'opportunity': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getMarginColor = (status) => {
    switch (status) {
      case 'critical': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'excellent': return 'text-green-600';
      default: return 'text-blue-600';
    }
  };

  const fetchData = async () => {
    try {
      // Add timestamp to prevent caching
      const t = Date.now();
      // Build branch filter params
      const branchParams = currentBranch 
        ? `&branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      
      const [purchasesRes, suppliesRes, suppliersRes, cajaChicaRes] = await Promise.all([
        api.get(`/purchases?t=${t}${branchParams}`),
        api.get(`/supplies?t=${t}${branchParams}`),
        api.get(`/suppliers?t=${t}${branchParams}`),
        api.get(`/finances/caja-chica?t=${t}${branchParams}`).catch(() => ({ data: { balance: 0 } }))
      ]);
      setPurchases(purchasesRes.data);
      setSupplies(suppliesRes.data);
      setSuppliers(suppliersRes.data);
      setCajaChicaBalance(cajaChicaRes.data?.balance || 0);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const addItemRow = () => {
    setPurchaseForm({
      ...purchaseForm,
      items: [...purchaseForm.items, { supply_id: '', quantity: '', unit_cost: '' }]
    });
  };

  const updateItem = (index, field, value) => {
    const updated = [...purchaseForm.items];
    updated[index][field] = value;
    
    // Auto-fill unit_cost from supply when selecting
    if (field === 'supply_id' && value) {
      const supply = supplies.find(s => s.supply_id === value);
      if (supply) {
        updated[index].unit_cost = supply.cost_per_unit.toString();
      }
    }
    
    setPurchaseForm({ ...purchaseForm, items: updated });
  };

  const removeItem = (index) => {
    setPurchaseForm({
      ...purchaseForm,
      items: purchaseForm.items.filter((_, i) => i !== index)
    });
  };

  const calculateTotal = () => {
    return purchaseForm.items.reduce((total, item) => {
      if (item.quantity && item.unit_cost) {
        return total + (parseFloat(item.quantity) * parseFloat(item.unit_cost));
      }
      return total;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) return;
    
    const validItems = purchaseForm.items.filter(item => 
      item.supply_id && item.quantity && item.unit_cost
    );
    
    if (validItems.length === 0) {
      toast.error('Agrega al menos un insumo');
      return;
    }
    
    if (!purchaseForm.supplier) {
      toast.error('Ingresa el proveedor');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/purchases', {
        supplier: purchaseForm.supplier,
        invoice_number: purchaseForm.invoice_number || null,
        payment_method: purchaseForm.payment_method,
        payment_source: purchaseForm.payment_source,
        notes: purchaseForm.notes || null,
        fecha: purchaseForm.fecha || null,
        items: validItems.map(item => ({
          supply_id: item.supply_id,
          quantity: parseFloat(item.quantity),
          unit_cost: parseFloat(item.unit_cost)
        }))
      });
      
      toast.success('Compra registrada. Stock actualizado.');
      setPurchaseDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar compra');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setPurchaseForm({
      supplier: '',
      invoice_number: '',
      payment_method: 'efectivo',
      payment_source: 'caja_principal',
      notes: '',
      items: [],
      fecha: new Date().toISOString().slice(0, 10)
    });
  };

  const openNewPurchase = () => {
    resetForm();
    addItemRow(); // Start with one empty row
    setPurchaseDialogOpen(true);
  };

  const viewPurchase = (purchase) => {
    setViewingPurchase(purchase);
    setViewDialogOpen(true);
  };

  const deletePurchase = async (purchaseId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta compra? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      await api.delete(`/purchases/${purchaseId}`);
      toast.success('Compra eliminada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar compra');
    }
  };

  const openEditDialog = (purchase) => {
    setEditingPurchase(purchase);
    // Get date from purchase (either fecha field or created_at)
    const purchaseDate = purchase.fecha || (purchase.created_at ? purchase.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10));
    setEditForm({
      supplier: purchase.supplier || '',
      invoice_number: purchase.invoice_number || '',
      payment_method: purchase.payment_method || 'efectivo',
      notes: purchase.notes || '',
      fecha: purchaseDate,
      items: purchase.items ? purchase.items.map(item => ({
        supply_id: item.supply_id,
        quantity: item.quantity.toString(),
        unit_cost: item.unit_cost.toString(),
        supply_name: item.supply_name,
        unit: item.unit
      })) : []
    });
    setEditDialogOpen(true);
  };

  // Edit form item functions
  const addEditItemRow = () => {
    setEditForm({
      ...editForm,
      items: [...editForm.items, { supply_id: '', quantity: '', unit_cost: '', supply_name: '', unit: '' }]
    });
  };

  const updateEditItem = (index, field, value) => {
    const updated = [...editForm.items];
    updated[index][field] = value;
    
    // Auto-fill unit_cost and name from supply when selecting
    if (field === 'supply_id' && value) {
      const supply = supplies.find(s => s.supply_id === value);
      if (supply) {
        updated[index].unit_cost = supply.cost_per_unit.toString();
        updated[index].supply_name = supply.name;
        updated[index].unit = supply.unit;
      }
    }
    
    setEditForm({ ...editForm, items: updated });
  };

  const removeEditItem = (index) => {
    setEditForm({
      ...editForm,
      items: editForm.items.filter((_, i) => i !== index)
    });
  };

  const calculateEditTotal = () => {
    return editForm.items.reduce((total, item) => {
      if (item.quantity && item.unit_cost) {
        return total + (parseFloat(item.quantity) * parseFloat(item.unit_cost));
      }
      return total;
    }, 0);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingPurchase) return;
    
    // Validate items
    const validItems = editForm.items.filter(item => 
      item.supply_id && item.quantity && item.unit_cost
    );
    
    if (validItems.length === 0) {
      toast.error('Agrega al menos un insumo');
      return;
    }
    
    try {
      await api.put(`/purchases/${editingPurchase.purchase_id}`, {
        supplier: editForm.supplier,
        invoice_number: editForm.invoice_number,
        payment_method: editForm.payment_method,
        notes: editForm.notes,
        fecha: editForm.fecha,
        items: validItems.map(item => ({
          supply_id: item.supply_id,
          quantity: parseFloat(item.quantity),
          unit_cost: parseFloat(item.unit_cost)
        }))
      });
      toast.success('Compra actualizada');
      setEditDialogOpen(false);
      setEditingPurchase(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar compra');
    }
  };

  // Pay a fiado purchase
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [purchaseToPay, setPurchaseToPay] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');

  const openPayModal = (purchase) => {
    setPurchaseToPay(purchase);
    setPaymentMethod('efectivo');
    setPayModalOpen(true);
  };

  const handlePayFiado = async () => {
    if (!purchaseToPay) return;
    
    try {
      await api.put(`/purchases/${purchaseToPay.purchase_id}`, {
        payment_method: paymentMethod
      });
      toast.success('Compra pagada correctamente');
      setPayModalOpen(false);
      setPurchaseToPay(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pagar compra');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter purchases by date (respeta zona horaria de Sonora ya configurada)
  const filteredPurchases = purchases.filter(purchase => {
    if (!dateFilter.startDate && !dateFilter.endDate) return true;
    
    // Usar la fecha ya formateada para comparar
    const purchaseDate = new Date(purchase.created_at);
    const purchaseDateOnly = new Date(purchaseDate.getFullYear(), purchaseDate.getMonth(), purchaseDate.getDate());
    
    if (dateFilter.startDate) {
      const [year, month, day] = dateFilter.startDate.split('-').map(Number);
      const startDate = new Date(year, month - 1, day);
      if (purchaseDateOnly < startDate) return false;
    }
    
    if (dateFilter.endDate) {
      const [year, month, day] = dateFilter.endDate.split('-').map(Number);
      const endDate = new Date(year, month - 1, day);
      if (purchaseDateOnly > endDate) return false;
    }
    
    return true;
  });

  // Download daily purchase format PDF
  const downloadDailyFormat = async () => {
    setDownloadingFormat(true);
    try {
      const response = await api.get('/purchase-formats/daily-format/pdf', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MR_FROGS_Formato_Compras_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Formato descargado correctamente');
    } catch (error) {
      console.error('Error downloading format:', error);
      toast.error('Error al descargar el formato');
    } finally {
      setDownloadingFormat(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-chivo font-extrabold" data-testid="purchases-title">
                Compras
              </h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Gestión de compras e inteligencia de proveedores</span>
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isMatriz ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  <Building2 className="w-3 h-3" />
                  {isMatriz ? 'Matriz' : branchName}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b">
            <button
              onClick={() => setActiveTab('compras')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'compras' 
                  ? 'border-green-600 text-green-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <ShoppingBag className="w-4 h-4 inline mr-2" />
              Mis Compras
            </button>
            <button
              onClick={() => setActiveTab('agente')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'agente' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Truck className="w-4 h-4 inline mr-2" />
              Agente Compras
            </button>
            <button
              onClick={() => setActiveTab('costos')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'costos' 
                  ? 'border-indigo-600 text-indigo-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <DollarSign className="w-4 h-4 inline mr-2" />
              Agente Costos
            </button>
          </div>

          {activeTab === 'compras' && (
            <>
              {/* Action Buttons */}
              <div className="flex justify-end gap-2 mb-6">
                <Button 
                  variant="outline"
                  onClick={downloadDailyFormat}
                  disabled={downloadingFormat}
                  data-testid="download-format-btn"
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingFormat ? 'Descargando...' : 'Formato Diario'}
                </Button>
                
                {isMatriz ? (
                  <Button 
                    className="bg-accent text-white hover:bg-accent/90"
                    onClick={openNewPurchase}
                    data-testid="new-purchase-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Compra
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Las compras se realizan desde Matriz</span>
                  </div>
                )}
              </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ShoppingBag className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total Compras</p>
                  <p className="text-2xl font-bold text-gray-900">{purchases.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Este Mes</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${purchases
                      .filter(p => new Date(p.created_at).getMonth() === new Date().getMonth())
                      .reduce((sum, p) => sum + p.total, 0)
                      .toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white border rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Promedio</p>
                  <p className="text-2xl font-bold text-purple-600">
                    ${purchases.length > 0 
                      ? Math.round(purchases.reduce((sum, p) => sum + p.total, 0) / purchases.length).toLocaleString()
                      : '0'}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white border rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Última Compra</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {purchases[0] ? `$${purchases[0].total.toLocaleString()}` : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <h2 className="font-bold flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Historial de Compras
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/80">Desde:</span>
                  <Input
                    type="date"
                    value={dateFilter.startDate}
                    onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                    className="w-36 h-8 text-sm bg-white text-gray-900 border-0 rounded"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/80">Hasta:</span>
                  <Input
                    type="date"
                    value={dateFilter.endDate}
                    onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                    className="w-36 h-8 text-sm bg-white text-gray-900 border-0 rounded"
                  />
                </div>
                {(dateFilter.startDate || dateFilter.endDate) && (
                  <button
                    onClick={() => setDateFilter({ startDate: '', endDate: '' })}
                    className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-sm flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Limpiar
                  </button>
                )}
                <span className="bg-white/20 px-3 py-1 rounded text-sm font-medium">
                  {filteredPurchases.length} compras
                </span>
              </div>
            </div>
            
            {/* Table Header */}
            <div className="bg-gray-50 border-b px-4 py-3 grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
              <div className="col-span-2">Fecha</div>
              <div className="col-span-3">Proveedor</div>
              <div className="col-span-1">Factura</div>
              <div className="col-span-1 text-center">Pago</div>
              <div className="col-span-1 text-center">Items</div>
              <div className="col-span-2 text-right">Total</div>
              <div className="col-span-2 text-center">Acciones</div>
            </div>
            
            {/* Table Body */}
            <div className="divide-y max-h-[calc(100vh-420px)] overflow-y-auto">
              {filteredPurchases.map(purchase => (
                <div 
                  key={purchase.purchase_id}
                  className="px-4 py-3 grid grid-cols-12 gap-2 items-center hover:bg-gray-50 transition-colors"
                >
                  {/* Date */}
                  <div className="col-span-2">
                    <span className="text-sm text-gray-900">{formatDate(purchase.created_at)}</span>
                  </div>
                  
                  {/* Supplier */}
                  <div className="col-span-3">
                    <span className="font-medium text-gray-900 truncate block">{purchase.supplier}</span>
                  </div>
                  
                  {/* Invoice */}
                  <div className="col-span-1">
                    {purchase.invoice_number ? (
                      <span className="text-sm text-gray-700 truncate block">{purchase.invoice_number}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                  
                  {/* Payment */}
                  <div className="col-span-1 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      purchase.payment_method === 'efectivo' ? 'bg-green-100 text-green-700' :
                      purchase.payment_method === 'tarjeta_debito' ? 'bg-blue-100 text-blue-700' :
                      purchase.payment_method === 'tarjeta_credito' ? 'bg-purple-100 text-purple-700' :
                      purchase.payment_method === 'transferencia' ? 'bg-cyan-100 text-cyan-700' :
                      purchase.payment_method === 'credito' || purchase.payment_method === 'fiado' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {purchase.payment_method === 'efectivo' ? 'Efect.' :
                       purchase.payment_method === 'tarjeta_debito' ? 'Débito' :
                       purchase.payment_method === 'tarjeta_credito' ? 'Créd.' :
                       purchase.payment_method === 'transferencia' ? 'Transf.' :
                       purchase.payment_method === 'credito' || purchase.payment_method === 'fiado' ? 'Fiado' :
                       purchase.payment_method || 'Efect.'}
                    </span>
                  </div>
                  
                  {/* Items */}
                  <div className="col-span-1 text-center">
                    <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-xs font-medium">
                      {purchase.items.length}
                    </span>
                  </div>
                  
                  {/* Total */}
                  <div className="col-span-2 text-right">
                    <span className="font-bold text-accent">${purchase.total.toLocaleString()}</span>
                  </div>
                  
                  {/* Actions */}
                  <div className="col-span-2 flex items-center justify-center gap-1">
                    <button
                      className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                      onClick={() => viewPurchase(purchase)}
                      title="Ver detalle"
                    >
                      <Eye className="w-4 h-4 text-gray-600" />
                    </button>
                    {isMatriz && (
                      <>
                        <button
                          className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
                          onClick={() => openEditDialog(purchase)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4 text-blue-500" />
                        </button>
                        {(purchase.payment_method === 'fiado' || purchase.payment_method === 'credito') && (
                          <button
                            className="p-1.5 hover:bg-green-100 rounded-lg transition-colors"
                            onClick={() => openPayModal(purchase)}
                            title="Pagar"
                          >
                            <DollarSign className="w-4 h-4 text-green-500" />
                          </button>
                        )}
                        <button
                          className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                          onClick={() => deletePurchase(purchase.purchase_id)}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Empty State */}
            {filteredPurchases.length === 0 && !loading && (
              <div className="py-12 text-center">
                <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">
                  {purchases.length === 0 ? 'No hay compras registradas' : 'No hay compras en el rango de fechas seleccionado'}
                </p>
                {purchases.length === 0 && (
                  <Button 
                    className="mt-4 bg-accent text-white hover:bg-accent/90"
                    onClick={openNewPurchase}
                  >
                    Registrar Primera Compra
                  </Button>
                )}
              </div>
            )}
          </div>
            </>
          )}

          {/* Agent Tab Content */}
          {activeTab === 'agente' && (
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              {agentLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                </div>
              ) : (
                <div className="flex h-[calc(100vh-250px)]">
                  {/* Left - Dashboard */}
                  <div className="w-1/2 p-4 overflow-auto border-r bg-gray-50">
                    {/* Summary Cards - Clickeable */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div 
                        className={`bg-white rounded-lg p-3 border cursor-pointer transition-all hover:shadow-md ${expandedSection === 'proveedores' ? 'ring-2 ring-blue-500' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'proveedores' ? null : 'proveedores')}
                      >
                        <div className="text-xs text-gray-500 flex items-center gap-1"><Truck className="w-3 h-3" /> Proveedores</div>
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-bold">{agentDashboard?.summary?.total_suppliers || 0}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'proveedores' ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                      <div 
                        className={`bg-white rounded-lg p-3 border cursor-pointer transition-all hover:shadow-md ${expandedSection === 'gastado' ? 'ring-2 ring-green-500' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'gastado' ? null : 'gastado')}
                      >
                        <div className="text-xs text-gray-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Gastado (6m)</div>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold">${(agentDashboard?.summary?.total_spent_6months || 0).toLocaleString()}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'gastado' ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                        <div className="text-xs text-green-600 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Ahorro Potencial</div>
                        <div className="text-lg font-bold text-green-600">${(agentDashboard?.summary?.potential_savings || 0).toLocaleString()}</div>
                      </div>
                      <div 
                        className={`bg-orange-50 rounded-lg p-3 border border-orange-200 cursor-pointer transition-all hover:shadow-md ${expandedSection === 'urgentes' ? 'ring-2 ring-orange-500' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'urgentes' ? null : 'urgentes')}
                      >
                        <div className="text-xs text-orange-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Necesitan Stock</div>
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-bold text-orange-600">{agentDashboard?.summary?.items_needing_stock || 0}</span>
                          <ChevronDown className={`w-4 h-4 text-orange-400 transition-transform ${expandedSection === 'urgentes' ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                    </div>

                    {/* Expanded: Proveedores */}
                    {expandedSection === 'proveedores' && (
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mb-4 animate-in slide-in-from-top-2">
                        <h4 className="font-bold text-sm text-blue-800 mb-2">📋 Lista de Proveedores</h4>
                        <div className="space-y-2">
                          {agentDashboard?.supplier_ranking?.map((s, idx) => (
                            <div 
                              key={s.name} 
                              className={`bg-white p-3 rounded-lg border cursor-pointer hover:shadow-md transition-all ${selectedSupplier === s.name ? 'ring-2 ring-blue-500' : ''}`}
                              onClick={() => setSelectedSupplier(selectedSupplier === s.name ? null : s.name)}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg font-bold text-gray-400">#{idx + 1}</span>
                                  <div>
                                    <div className="font-medium">{s.name}</div>
                                    <div className="text-xs text-gray-500">{s.purchase_count} compras · {s.items_variety} productos</div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-green-600">${s.total_spent.toLocaleString()}</div>
                                  <div className={`px-2 py-0.5 rounded text-xs font-bold inline-block ${getScoreColor(s.overall_score)}`}>
                                    Score: {s.overall_score}
                                  </div>
                                </div>
                              </div>
                              {selectedSupplier === s.name && (
                                <div className="mt-3 pt-3 border-t">
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="bg-gray-50 p-2 rounded">
                                      <span className="text-gray-500">Mejor día:</span>
                                      <span className="font-medium ml-1">{s.most_active_day}</span>
                                    </div>
                                    <div className="bg-gray-50 p-2 rounded">
                                      <span className="text-gray-500">Precio:</span>
                                      <span className="font-medium ml-1">{'⭐'.repeat(Math.min(5, Math.ceil(s.price_rating / 2)))}</span>
                                    </div>
                                  </div>
                                  <div className="mt-2 text-xs text-gray-600 bg-yellow-50 p-2 rounded">
                                    💡 Productos: {agentDashboard?.item_analysis?.filter(i => i.best_supplier === s.name).map(i => i.name).slice(0, 3).join(', ') || 'Ver historial'}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Expanded: Gastado */}
                    {expandedSection === 'gastado' && (
                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3 mb-4 animate-in slide-in-from-top-2">
                        <h4 className="font-bold text-sm text-green-800 mb-2">💰 Desglose de Gastos (6 meses)</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-white p-3 rounded-lg border">
                            <div className="text-xs text-gray-500 mb-2">Por Proveedor</div>
                            <div className="space-y-2">
                              {agentDashboard?.supplier_ranking?.slice(0, 5).map((s) => {
                                const pct = agentDashboard?.summary?.total_spent_6months > 0 
                                  ? (s.total_spent / agentDashboard.summary.total_spent_6months * 100).toFixed(0)
                                  : 0;
                                return (
                                  <div key={s.name} className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="bg-green-500 h-2 rounded-full" 
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <span className="text-xs w-20 truncate">{s.name}</span>
                                    <span className="text-xs font-bold w-12 text-right">{pct}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="bg-white p-3 rounded-lg border">
                            <div className="text-xs text-gray-500 mb-2">Resumen</div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Total 6 meses:</span>
                                <span className="font-bold">${(agentDashboard?.summary?.total_spent_6months || 0).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Promedio mensual:</span>
                                <span className="font-bold">${((agentDashboard?.summary?.total_spent_6months || 0) / 6).toFixed(0)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Total compras:</span>
                                <span className="font-bold">{agentDashboard?.summary?.total_purchases || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Expanded: Urgentes */}
                    {expandedSection === 'urgentes' && (
                      <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-3 mb-4 animate-in slide-in-from-top-2">
                        <h4 className="font-bold text-sm text-orange-800 mb-2">⚠️ Items que Necesitan Stock</h4>
                        <div className="space-y-2 max-h-60 overflow-auto">
                          {agentDashboard?.stock_needs?.map((item) => (
                            <div key={item.supply_id} className="bg-white p-3 rounded-lg border flex justify-between items-center">
                              <div>
                                <div className="font-medium text-sm">{item.name || 'Sin nombre'}</div>
                                <div className="text-xs text-gray-500">
                                  Stock: {item.current_stock} / Mín: {item.min_stock} {item.unit}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-orange-600">{item.suggested_qty} {item.unit}</div>
                                {getUrgencyBadge(item.urgency)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Plan Semanal Detallado */}
                    <div className="bg-white rounded-lg p-3 border">
                      <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-blue-500" /> 📅 Plan de Compras Semanal
                      </h3>
                      
                      {/* Week Header */}
                      <div className="bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-lg p-3 mb-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-xs opacity-80">Semana Actual</div>
                            <div className="font-bold">{new Date().toLocaleDateString('es-MX', { month: 'long', day: 'numeric' })} - {new Date(Date.now() + 6*24*60*60*1000).toLocaleDateString('es-MX', { month: 'long', day: 'numeric' })}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold">${((agentDashboard?.summary?.total_spent_6months || 0) / 6 / 4).toFixed(0)}</div>
                            <div className="text-xs opacity-80">Presupuesto Est.</div>
                          </div>
                        </div>
                      </div>

                      {/* Days */}
                      <div className="space-y-2">
                        {agentDashboard?.purchase_calendar?.map((day) => (
                          <div key={day.day} className="border rounded-lg overflow-hidden">
                            <button
                              onClick={() => setExpandedDay(expandedDay === day.day ? null : day.day)}
                              className="w-full flex items-center justify-between p-3 bg-green-50 hover:bg-green-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">
                                  {day.day.substring(0, 3)}
                                </div>
                                <div className="text-left">
                                  <div className="font-bold text-sm">{day.day}</div>
                                  <div className="text-xs text-gray-500">{day.suppliers.length} proveedor(es)</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-green-600 font-bold text-sm">
                                  +${day.suppliers.reduce((sum, s) => sum + s.potential_savings, 0)} ahorro
                                </span>
                                <ChevronDown className={`w-5 h-5 transition-transform ${expandedDay === day.day ? 'rotate-180' : ''}`} />
                              </div>
                            </button>
                            
                            {expandedDay === day.day && (
                              <div className="p-3 bg-white border-t">
                                {day.suppliers.map((sup) => (
                                  <div key={sup.name} className="mb-3 last:mb-0">
                                    <div className="flex justify-between items-center mb-2 pb-2 border-b">
                                      <div className="font-bold text-sm text-gray-800">{sup.name}</div>
                                      <span className="text-green-600 text-xs font-medium bg-green-100 px-2 py-1 rounded">
                                        Ahorro: ${sup.potential_savings}
                                      </span>
                                    </div>
                                    
                                    {/* Products Table */}
                                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                                      <table className="w-full text-xs">
                                        <thead className="bg-gray-200">
                                          <tr>
                                            <th className="text-left p-2">Producto</th>
                                            <th className="text-right p-2">Precio</th>
                                            <th className="text-right p-2">Stock</th>
                                            <th className="text-right p-2">Comprar</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {sup.items.map((itemName, idx) => {
                                            const itemData = agentDashboard?.item_analysis?.find(i => i.name === itemName);
                                            const stockData = agentDashboard?.stock_needs?.find(s => s.name === itemName);
                                            return (
                                              <tr key={idx} className="border-b border-gray-100">
                                                <td className="p-2 font-medium">{itemName}</td>
                                                <td className="p-2 text-right">${itemData?.best_supplier_price || itemData?.avg_price || '-'}</td>
                                                <td className="p-2 text-right">
                                                  {stockData ? (
                                                    <span className={stockData.urgency === 'urgent' ? 'text-red-600' : ''}>
                                                      {stockData.current_stock}/{stockData.min_stock}
                                                    </span>
                                                  ) : '-'}
                                                </td>
                                                <td className="p-2 text-right font-bold text-green-600">
                                                  {stockData?.suggested_qty || '-'} {stockData?.unit || ''}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                    
                                    <div className="flex gap-2 mt-2">
                                      <button 
                                        onClick={() => generateShoppingList(day, sup.name, sup.items)}
                                        className="flex-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 py-2 rounded font-medium transition-colors"
                                      >
                                        📋 Generar Lista
                                      </button>
                                      <button 
                                        onClick={() => markPurchaseCompleted(day.day, sup.name)}
                                        className={`flex-1 text-xs py-2 rounded font-medium transition-colors ${
                                          completedPurchases[`${day.day}_${sup.name}`]
                                            ? 'bg-green-600 text-white hover:bg-green-700'
                                            : 'bg-green-100 hover:bg-green-200 text-green-700'
                                        }`}
                                      >
                                        {completedPurchases[`${day.day}_${sup.name}`] ? '✓ Completado' : '☐ Marcar Completado'}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {(!agentDashboard?.purchase_calendar || agentDashboard.purchase_calendar.length === 0) && (
                          <div className="text-center py-6 text-gray-500">
                            <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">Registra más compras para generar</p>
                            <p className="text-xs">recomendaciones de calendario</p>
                          </div>
                        )}
                      </div>

                      {/* Urgent Today */}
                      {agentDashboard?.stock_needs?.filter(n => n.urgency === 'urgent').length > 0 && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="font-bold text-sm text-red-700 mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" /> ⚡ Compras Urgentes HOY
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {agentDashboard.stock_needs.filter(n => n.urgency === 'urgent').slice(0, 4).map((item) => (
                              <div key={item.supply_id} className="bg-white p-2 rounded border border-red-200 flex justify-between items-center">
                                <span className="text-xs font-medium truncate">{item.name}</span>
                                <span className="text-xs font-bold text-red-600 ml-2">{item.suggested_qty} {item.unit}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right - Chat */}
                  <div className="w-1/2 flex flex-col">
                    <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-sm">Chat con Agente</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={fetchAgentDashboard}>
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex-1 overflow-auto p-4 space-y-3">
                      {chatMessages.length === 0 && (
                        <div className="text-center text-gray-500 py-6">
                          <Bot className="w-10 h-10 mx-auto mb-2 text-green-300" />
                          <p className="font-medium text-sm">Agente de Compras</p>
                          <p className="text-xs">Pregúntame sobre proveedores o precios</p>
                          <div className="mt-3 space-y-1">
                            {['¿Quién me da mejor precio en camarón?', '¿Qué día compro mariscos?', '¿Cómo reduzco costos?'].map(q => (
                              <button key={q} onClick={() => setChatInput(q)} className="block w-full text-left px-3 py-2 bg-green-50 hover:bg-green-100 rounded text-xs text-green-700">
                                💡 {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {chatMessages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] p-3 rounded-lg text-sm ${msg.role === 'user' ? 'bg-green-600 text-white' : 'bg-gray-100'}`}>
                            <div className="whitespace-pre-wrap">{msg.content}</div>
                          </div>
                        </div>
                      ))}
                      {chatLoading && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 p-3 rounded-lg flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-xs">Analizando...</span>
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                    <div className="p-3 border-t">
                      <div className="flex gap-2">
                        <Input
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && sendAgentMessage()}
                          placeholder="Pregunta sobre proveedores..."
                          disabled={chatLoading}
                          className="flex-1 text-sm"
                        />
                        <Button onClick={sendAgentMessage} disabled={chatLoading || !chatInput.trim()} className="bg-green-600 hover:bg-green-700">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cost Agent Tab */}
          {activeTab === 'costos' && (
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 280px)' }}>
              {costLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : (
                <div className="flex h-full">
                  {/* Left - Dashboard */}
                  <div className="w-1/2 p-4 overflow-auto border-r">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <div className="bg-white rounded-lg p-3 shadow-sm border">
                        <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                          <Package className="w-4 h-4" /> Insumos
                        </div>
                        <div className="text-2xl font-bold">{costDashboard?.summary?.total_supplies || 0}</div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 shadow-sm border border-red-200">
                        <div className="flex items-center gap-2 text-red-600 text-xs mb-1">
                          <AlertTriangle className="w-4 h-4" /> Críticos
                        </div>
                        <div className="text-2xl font-bold text-red-600">{costDashboard?.summary?.critical_alerts || 0}</div>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-3 shadow-sm border border-yellow-200">
                        <div className="flex items-center gap-2 text-yellow-600 text-xs mb-1">
                          <TrendingUp className="w-4 h-4" /> Alertas
                        </div>
                        <div className="text-2xl font-bold text-yellow-600">{costDashboard?.summary?.warning_alerts || 0}</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 shadow-sm border border-green-200">
                        <div className="flex items-center gap-2 text-green-600 text-xs mb-1">
                          <CheckCircle className="w-4 h-4" /> Margen
                        </div>
                        <div className="text-2xl font-bold text-green-600">{costDashboard?.summary?.avg_margin_pct || 0}%</div>
                      </div>
                    </div>

                    {/* Critical Supplies */}
                    {costDashboard?.critical_supplies?.length > 0 && (
                      <div className="bg-white rounded-lg p-4 shadow-sm mb-4 border-l-4 border-red-500">
                        <h3 className="font-bold text-red-700 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> Insumos Críticos (+30%)
                        </h3>
                        <div className="space-y-2">
                          {costDashboard.critical_supplies.map(s => (
                            <div key={s.supply_id} className="flex justify-between items-center text-sm p-2 bg-red-50 rounded">
                              <span className="font-medium">{s.name}</span>
                              <div className="text-right">
                                <span className="text-red-600 font-bold">+{s.variation_pct}%</span>
                                <span className="text-gray-500 ml-2">${s.current_cost}/{s.unit}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Low Margin Products */}
                    {costDashboard?.low_margin_products?.length > 0 && (
                      <div className="bg-white rounded-lg p-4 shadow-sm mb-4 border-l-4 border-yellow-500">
                        <h3 className="font-bold text-yellow-700 mb-2 flex items-center gap-2">
                          <TrendingDown className="w-4 h-4" /> Productos Bajo Margen
                        </h3>
                        <div className="space-y-2">
                          {costDashboard.low_margin_products.slice(0, 5).map(p => (
                            <div 
                              key={p.product_id} 
                              onClick={() => setSelectedProduct(p)}
                              className="flex justify-between items-center text-sm p-2 bg-yellow-50 rounded cursor-pointer hover:bg-yellow-100 transition-colors"
                            >
                              <span className="font-medium truncate flex-1">{p.name}</span>
                              <div className="text-right ml-2 flex items-center gap-2">
                                <span className={`font-bold ${getMarginColor(p.margin_status)}`}>{p.margin_pct}%</span>
                                <span className="text-gray-500">${p.price}</span>
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* All Supplies Toggle */}
                    <div className="bg-white rounded-lg shadow-sm mb-4">
                      <button 
                        onClick={() => setShowAllSupplies(!showAllSupplies)}
                        className="w-full p-3 flex justify-between items-center hover:bg-gray-50"
                      >
                        <span className="font-medium">Ver todos los insumos ({costDashboard?.all_supplies?.length || 0})</span>
                        {showAllSupplies ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                      {showAllSupplies && (
                        <div className="p-3 border-t max-h-60 overflow-auto">
                          {costDashboard?.all_supplies?.map(s => (
                            <div key={s.supply_id} className={`flex justify-between items-center text-sm p-2 mb-1 rounded border ${getAlertColor(s.alert_level)}`}>
                              <span>{s.name}</span>
                              <div>
                                <span className="font-medium">${s.current_cost}/{s.unit}</span>
                                {s.variation_pct !== 0 && (
                                  <span className={`ml-2 ${s.variation_pct > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {s.variation_pct > 0 ? '+' : ''}{s.variation_pct}%
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right - Chat */}
                  <div className="w-1/2 flex flex-col">
                    <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5 text-indigo-600" />
                        <span className="font-medium text-sm">Chat con Agente de Costos</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={fetchCostDashboard}>
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex-1 overflow-auto p-4 space-y-3">
                      {costChatMessages.length === 0 && (
                        <div className="text-center text-gray-500 py-6">
                          <Bot className="w-10 h-10 mx-auto mb-2 text-indigo-300" />
                          <p className="font-medium text-sm">Agente de Costos</p>
                          <p className="text-xs">Pregúntame sobre márgenes o análisis</p>
                          <div className="mt-3 space-y-1">
                            {['¿Cuáles insumos me afectan más?', '¿Qué productos debo subir de precio?', 'Dame análisis general de costos'].map(q => (
                              <button key={q} onClick={() => setCostChatInput(q)} className="block w-full text-left px-3 py-2 bg-indigo-50 hover:bg-indigo-100 rounded text-xs text-indigo-700">
                                💡 {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {costChatMessages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] p-3 rounded-lg text-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-gray-100'}`}>
                            <div className="whitespace-pre-wrap">{msg.content}</div>
                          </div>
                        </div>
                      ))}
                      {costChatLoading && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 p-3 rounded-lg flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-xs">Analizando...</span>
                          </div>
                        </div>
                      )}
                      <div ref={costChatEndRef} />
                    </div>
                    <div className="p-3 border-t">
                      <div className="flex gap-2">
                        <Input
                          value={costChatInput}
                          onChange={(e) => setCostChatInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && sendCostMessage()}
                          placeholder="Pregunta sobre costos..."
                          disabled={costChatLoading}
                          className="flex-1 text-sm"
                        />
                        <Button onClick={sendCostMessage} disabled={costChatLoading || !costChatInput.trim()} className="bg-indigo-600 hover:bg-indigo-700">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* New Purchase Modal */}
      <Modal
        open={purchaseDialogOpen}
        onClose={() => setPurchaseDialogOpen(false)}
        title="Nueva Compra"
        wide
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Proveedor * - Escribe para buscar</Label>
              <SearchSelect
                options={suppliers.map(s => ({
                  ...s,
                  displayName: `${s.name}${s.phone ? ' - ' + s.phone : ''}`
                }))}
                value={suppliers.find(s => s.name === purchaseForm.supplier)?.supplier_id || ''}
                onChange={(val) => {
                  const selectedSupplier = suppliers.find(s => s.supplier_id === val);
                  setPurchaseForm({ ...purchaseForm, supplier: selectedSupplier?.name || '' });
                }}
                placeholder="Escribe el nombre del proveedor..."
                displayKey="displayName"
                valueKey="supplier_id"
                renderOption={(opt) => (
                  <div className="flex justify-between items-center w-full">
                    <span className="font-medium">{opt.name}</span>
                    {opt.phone && <span className="text-muted-foreground text-xs">{opt.phone}</span>}
                  </div>
                )}
              />
              {suppliers.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No hay proveedores. <a href="/suppliers" className="text-accent underline">Agregar proveedor</a>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Número de Factura</Label>
              <Input
                value={purchaseForm.invoice_number}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, invoice_number: e.target.value })}
                placeholder="Opcional"
              />
            </div>
          </div>

          {/* Fecha de Compra */}
          <div className="space-y-2">
            <Label>Fecha de Compra</Label>
            <Input
              type="date"
              value={purchaseForm.fecha}
              onChange={(e) => setPurchaseForm({ ...purchaseForm, fecha: e.target.value })}
              className="w-full max-w-xs"
            />
          </div>

          {/* Payment Method - FUDO Style */}
          <div className="space-y-2">
            <Label>Método de Pago *</Label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'efectivo', label: 'Efectivo', icon: '💵' },
                { value: 'tarjeta_debito', label: 'Tarjeta Débito', icon: '💳' },
                { value: 'tarjeta_credito', label: 'Tarjeta Crédito', icon: '💳' },
                { value: 'transferencia', label: 'Transferencia', icon: '🏦' },
                { value: 'cheque', label: 'Cheque', icon: '📝' },
                { value: 'credito', label: 'Crédito (Fiado)', icon: '📋' }
              ].map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setPurchaseForm({ ...purchaseForm, payment_method: method.value })}
                  className={`px-4 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                    purchaseForm.payment_method === method.value
                      ? 'border-accent bg-accent/10 text-accent font-bold'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span>{method.icon}</span>
                  <span className="text-sm">{method.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Payment Source - Caja Principal / Caja Chica */}
          <div className="space-y-2">
            <Label>Pagar de *</Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPurchaseForm({ ...purchaseForm, payment_source: 'caja_principal' })}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all flex flex-col items-center justify-center gap-1 ${
                  purchaseForm.payment_source === 'caja_principal'
                    ? 'border-green-500 bg-green-50 text-green-700 font-bold'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">🏦</span>
                  <span>Caja Principal</span>
                </div>
                <span className="text-xs text-gray-500">Ventas del día</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (calculateTotal() > cajaChicaBalance) {
                    toast.error(`Saldo insuficiente en Caja Chica. Disponible: $${cajaChicaBalance.toFixed(2)}`);
                    return;
                  }
                  setPurchaseForm({ ...purchaseForm, payment_source: 'caja_chica' });
                }}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all flex flex-col items-center justify-center gap-1 ${
                  purchaseForm.payment_source === 'caja_chica'
                    ? 'border-orange-500 bg-orange-50 text-orange-700 font-bold'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">💰</span>
                  <span>Caja Chica</span>
                </div>
                <span className={`text-sm font-bold ${cajaChicaBalance > 0 ? 'text-orange-600' : 'text-red-500'}`}>
                  ${cajaChicaBalance.toFixed(2)} disponible
                </span>
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Input
              value={purchaseForm.notes}
              onChange={(e) => setPurchaseForm({ ...purchaseForm, notes: e.target.value })}
              placeholder="Notas adicionales (opcional)"
            />
          </div>

          {/* Items */}
          <div className="space-y-3">
            <Label>Insumos</Label>
            {purchaseForm.items.map((item, idx) => (
              <div key={idx} className="flex gap-2 items-end p-3 bg-muted/30 rounded-lg">
                <div className="flex-1">
                  <Label className="text-xs">Insumo (escribe para buscar)</Label>
                  <SearchSelect
                    options={supplies.map(s => ({
                      ...s,
                      displayName: `${s.name} (${s.unit}) - $${s.cost_per_unit}`
                    }))}
                    value={item.supply_id}
                    onChange={(val) => updateItem(idx, 'supply_id', val)}
                    placeholder="Escribe para buscar insumo..."
                    displayKey="displayName"
                    valueKey="supply_id"
                    renderOption={(opt) => (
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{opt.name}</span>
                        <span className="text-muted-foreground text-xs">
                          {opt.unit} - ${opt.cost_per_unit}
                        </span>
                      </div>
                    )}
                  />
                </div>
                <div className="w-24">
                  <Label className="text-xs">Cantidad</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="w-28">
                  <Label className="text-xs">Costo Unit.</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.unit_cost}
                    onChange={(e) => updateItem(idx, 'unit_cost', e.target.value)}
                    placeholder="$0.00"
                  />
                </div>
                <div className="w-24 text-right">
                  <Label className="text-xs">Subtotal</Label>
                  <p className="h-10 flex items-center justify-end font-bold">
                    ${item.quantity && item.unit_cost 
                      ? (parseFloat(item.quantity) * parseFloat(item.unit_cost)).toFixed(2)
                      : '0.00'}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-destructive"
                  onClick={() => removeItem(idx)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={addItemRow}
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Insumo
            </Button>
          </div>

          {/* Total */}
          {purchaseForm.items.length > 0 && (
            <div className="p-4 bg-accent/10 rounded-lg flex justify-between items-center">
              <span className="font-bold">Total de la Compra:</span>
              <span className="text-2xl font-chivo font-extrabold text-accent">
                ${calculateTotal().toFixed(2)}
              </span>
            </div>
          )}

          {supplies.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay insumos registrados. Ve a <strong>Insumos</strong> para crear algunos primero.
            </p>
          )}

          <Button 
            type="submit" 
            className="w-full bg-accent text-white hover:bg-accent/90"
            disabled={purchaseForm.items.length === 0 || submitting}
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            {submitting ? 'Registrando...' : 'Registrar Compra'}
          </Button>
        </form>
      </Modal>

      {/* View Purchase Modal */}
      <Modal
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        title={`Compra - ${viewingPurchase?.supplier || ''}`}
        wide
      >
        {viewingPurchase && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Proveedor</p>
                <p className="font-bold">{viewingPurchase.supplier}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fecha</p>
                <p className="font-bold">{formatDate(viewingPurchase.created_at)}</p>
              </div>
              {viewingPurchase.invoice_number && (
                <div>
                  <p className="text-sm text-muted-foreground">Factura</p>
                  <p className="font-bold">{viewingPurchase.invoice_number}</p>
                </div>
              )}
              {viewingPurchase.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notas</p>
                  <p>{viewingPurchase.notes}</p>
                </div>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Insumo</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead className="text-right">Costo Unit.</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {viewingPurchase.items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{item.supply_name}</TableCell>
                    <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                    <TableCell className="text-right">${item.unit_cost.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-bold">${item.subtotal.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="p-4 bg-accent/10 rounded-lg flex justify-between items-center">
              <span className="font-bold">Total:</span>
              <span className="text-2xl font-chivo font-extrabold text-accent">
                ${viewingPurchase.total.toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Purchase Modal */}
      <Modal
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title="Editar Compra"
        wide
      >
        {editingPurchase && (
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Proveedor *</label>
                <Input
                  value={editForm.supplier}
                  onChange={(e) => setEditForm({...editForm, supplier: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">No. Factura (opcional)</label>
                <Input
                  value={editForm.invoice_number}
                  onChange={(e) => setEditForm({...editForm, invoice_number: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Fecha de Compra *
                </label>
                <Input
                  type="date"
                  value={editForm.fecha}
                  onChange={(e) => setEditForm({...editForm, fecha: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Método de Pago</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { value: 'efectivo', label: 'Efectivo' },
                  { value: 'tarjeta', label: 'Tarjeta' },
                  { value: 'transferencia', label: 'Transferencia' },
                  { value: 'credito', label: 'Fiado' }
                ].map(method => (
                  <button
                    key={method.value}
                    type="button"
                    className={`p-2 border rounded-lg text-sm transition-all ${
                      editForm.payment_method === method.value
                        ? 'border-accent bg-accent/10 text-accent font-bold'
                        : 'hover:border-gray-400'
                    }`}
                    onClick={() => setEditForm({...editForm, payment_method: method.value})}
                  >
                    {method.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Notas (opcional)</label>
              <Input
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
              />
            </div>

            {/* Items Section */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Insumos de la Compra</Label>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {editForm.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-end p-3 bg-muted/30 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-xs">Insumo</Label>
                      <SearchSelect
                        options={supplies.map(s => ({
                          ...s,
                          displayName: `${s.name} (${s.unit}) - $${s.cost_per_unit}`
                        }))}
                        value={item.supply_id}
                        onChange={(val) => updateEditItem(idx, 'supply_id', val)}
                        placeholder="Buscar insumo..."
                        displayKey="displayName"
                        valueKey="supply_id"
                        renderOption={(opt) => (
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{opt.name}</span>
                            <span className="text-muted-foreground text-xs">
                              {opt.unit} - ${opt.cost_per_unit}
                            </span>
                          </div>
                        )}
                      />
                    </div>
                    <div className="w-20">
                      <Label className="text-xs">Cantidad</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateEditItem(idx, 'quantity', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="w-24">
                      <Label className="text-xs">Costo Unit.</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.unit_cost}
                        onChange={(e) => updateEditItem(idx, 'unit_cost', e.target.value)}
                        placeholder="$0.00"
                      />
                    </div>
                    <div className="w-20 text-right">
                      <Label className="text-xs">Subtotal</Label>
                      <p className="h-10 flex items-center justify-end font-bold text-sm">
                        ${item.quantity && item.unit_cost 
                          ? (parseFloat(item.quantity) * parseFloat(item.unit_cost)).toFixed(2)
                          : '0.00'}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 text-destructive"
                      onClick={() => removeEditItem(idx)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={addEditItemRow}
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Insumo
              </Button>
            </div>

            {/* Total */}
            {editForm.items.length > 0 && (
              <div className="p-4 bg-accent/10 rounded-lg flex justify-between items-center">
                <span className="font-bold">Total de la Compra:</span>
                <span className="text-2xl font-chivo font-extrabold text-accent">
                  ${calculateEditTotal().toFixed(2)}
                </span>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setEditDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-accent text-white"
                disabled={editForm.items.length === 0}
              >
                Guardar Cambios
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Pay Fiado Purchase Modal */}
      <Modal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        title="Pagar Compra Fiado"
      >
        {purchaseToPay && (
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-orange-600">Compra a:</p>
              <p className="font-bold text-lg">{purchaseToPay.supplier}</p>
              <p className="text-2xl font-bold text-orange-600 mt-2">
                ${purchaseToPay.total?.toLocaleString()}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">¿Cómo pagas?</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'efectivo', label: 'Efectivo' },
                  { value: 'tarjeta', label: 'Tarjeta' },
                  { value: 'transferencia', label: 'Transferencia' }
                ].map(method => (
                  <button
                    key={method.value}
                    type="button"
                    className={`p-3 border rounded-lg text-sm transition-all ${
                      paymentMethod === method.value
                        ? 'border-green-500 bg-green-50 text-green-700 font-bold'
                        : 'hover:border-gray-400'
                    }`}
                    onClick={() => setPaymentMethod(method.value)}
                  >
                    {method.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-800">
              <p>⚠️ Al pagar, se creará el movimiento de salida en caja.</p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setPayModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handlePayFiado}
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Confirmar Pago
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Product Cost Detail Modal */}
      <Modal
        open={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        title={selectedProduct?.name || 'Detalle del Producto'}
        wide
      >
        {selectedProduct && (
          <div className="space-y-4">
            {/* Helper function to format currency */}
            {(() => {
              const formatMoney = (num) => Math.round(num || 0).toLocaleString('es-MX');
              const isLoss = (selectedProduct.price || 0) < (selectedProduct.cost || 0);
              const marginPct = Math.round(selectedProduct.margin_pct || 0);
              const profitLoss = Math.round((selectedProduct.price || 0) - (selectedProduct.cost || 0));
              
              return (
                <>
                  {/* Header with price and margin */}
                  <div className={`flex justify-between items-center p-4 rounded-lg border ${isLoss ? 'bg-gradient-to-r from-red-50 to-red-100 border-red-300' : 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200'}`}>
                    <div>
                      <div className="text-2xl font-bold">${formatMoney(selectedProduct.price)}</div>
                      <div className="text-sm text-gray-500">Precio de venta</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${isLoss ? 'text-red-600' : getMarginColor(selectedProduct.margin_status)}`}>
                        {marginPct}%
                      </div>
                      <div className="text-sm text-gray-500">Margen actual</div>
                    </div>
                  </div>

                  {/* Cost breakdown */}
                  <div className="bg-white rounded-lg border p-4">
                    <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4" /> Desglose de Costos
                    </h4>
                    
                    {selectedProduct.ingredients && selectedProduct.ingredients.length > 0 ? (
                      <div className="space-y-2">
                        {selectedProduct.ingredients.map((ing, idx) => {
                          const percentage = selectedProduct.cost > 0 
                            ? Math.round((ing.cost || 0) / selectedProduct.cost * 100)
                            : 0;
                          return (
                            <div key={idx} className="flex items-center gap-2">
                              <div className="flex-1">
                                <div className="flex justify-between text-sm">
                                  <span>{ing.name} ({ing.quantity} {ing.unit}) @ ${ing.unit_cost || 0}/{ing.unit}</span>
                                  <span className="font-medium">${formatMoney(ing.cost)}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                  <div 
                                    className="bg-orange-500 h-2 rounded-full" 
                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                  />
                                </div>
                              </div>
                              <span className="text-xs text-gray-500 w-10 text-right">{percentage}%</span>
                            </div>
                          );
                        })}
                        <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                          <span>Costo Total:</span>
                          <span className="text-orange-600">${formatMoney(selectedProduct.cost)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">Sin receta configurada</p>
                        <p className="text-xs">Agrega ingredientes para ver el desglose</p>
                </div>
              )}
            </div>

            {/* Suggestions */}
            <div className={`rounded-lg border p-4 ${isLoss ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
              <h4 className={`font-bold mb-2 flex items-center gap-2 ${isLoss ? 'text-red-700' : 'text-blue-700'}`}>
                {isLoss ? '⚠️ ALERTA: Vendiendo a Pérdida' : '💡 Sugerencias'}
              </h4>
              <div className="space-y-2 text-sm">
                {isLoss && (
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                    <span className="text-red-700 font-medium">
                      El costo (${formatMoney(selectedProduct.cost)}) supera el precio de venta (${formatMoney(selectedProduct.price)})
                    </span>
                  </div>
                )}
                {selectedProduct.margin_pct < 50 && (
                  <div className="flex items-start gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5" />
                    <span>
                      Para margen del 50%: Sube el precio a{' '}
                      <span className="font-bold text-blue-700">
                        ${formatMoney((selectedProduct.cost || 0) * 2)}
                      </span>
                    </span>
                  </div>
                )}
                {selectedProduct.margin_pct < 40 && !isLoss && (
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                    <span>Considera revisar proveedores para reducir costos de ingredientes</span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <DollarSign className={`w-4 h-4 mt-0.5 ${isLoss ? 'text-red-600' : 'text-green-600'}`} />
                  <span>
                    {isLoss ? 'Pérdida' : 'Ganancia'} por unidad:{' '}
                    <span className={`font-bold ${isLoss ? 'text-red-700' : 'text-green-700'}`}>
                      ${formatMoney(Math.abs(profitLoss))}
                      {isLoss && ' (PÉRDIDA)'}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setSelectedProduct(null);
                  window.location.href = `/products?edit=${selectedProduct.product_id}`;
                }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar Producto
              </Button>
              <Button 
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                onClick={() => {
                  setCostChatInput(`¿Por qué ${selectedProduct.name} tiene bajo margen y cómo puedo mejorarlo?`);
                  setSelectedProduct(null);
                }}
              >
                <Bot className="w-4 h-4 mr-2" />
                Preguntar al Agente
              </Button>
            </div>
                </>
              );
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Purchases;
